import { useState } from 'react';
import {
  ArrowLeft,
  Footprints,
  Flame,
  Droplets,
  UtensilsCrossed,
  RefreshCw,
  Calendar,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { healthDataService } from '../services/health-data.service';
import { LoadingSkeleton } from './LoadingSkeleton';
import toast from 'react-hot-toast';
import { handleApiError } from '../lib/api-client';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';
import { format, subDays } from 'date-fns';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

type Period = '7' | '30' | '90';

interface HistoricalDataProps {
  onBack: () => void;
}

export function HistoricalData({ onBack }: HistoricalDataProps) {
  const [period, setPeriod] = useState<Period>('30');

  const endDate = new Date();
  const startDate = subDays(endDate, parseInt(period, 10));
  const startDateStr = startDate.toISOString();
  const endDateStr = endDate.toISOString();

  const {
    data: analyticsData,
    isLoading: analyticsLoading,
    error: analyticsError,
    refetch: refetchAnalytics,
  } = useQuery({
    queryKey: ['analytics', period, startDateStr, endDateStr],
    queryFn: () =>
      healthDataService.getAnalytics({
        startDate: startDateStr,
        endDate: endDateStr,
      }),
  });

  const {
    data: stepEntriesData,
    isLoading: stepsLoading,
    refetch: refetchSteps,
  } = useQuery({
    queryKey: ['step-entries-history', period, startDateStr, endDateStr],
    queryFn: () =>
      healthDataService.getStepEntries({
        startDate: startDateStr,
        endDate: endDateStr,
        limit: 100,
      }),
  });

  const {
    data: glucoseData,
    isLoading: glucoseLoading,
    refetch: refetchGlucose,
  } = useQuery({
    queryKey: ['sugar-readings-history', period, startDateStr, endDateStr],
    queryFn: () =>
      healthDataService.getSugarReadings({
        startDate: startDateStr,
        endDate: endDateStr,
        limit: Math.min(Number(period) * 288, 5000),
      }),
  });

  const analytics = analyticsData?.data ?? analyticsData ?? {};
  const stepEntriesRaw = stepEntriesData?.data ?? stepEntriesData;
  const stepEntries = Array.isArray(stepEntriesRaw) ? stepEntriesRaw : [];
  const glucoseReadingsRaw = glucoseData?.data ?? glucoseData;
  const glucoseReadings = Array.isArray(glucoseReadingsRaw) ? glucoseReadingsRaw : [];

  const handleRefresh = () => {
    refetchAnalytics();
    refetchSteps();
    refetchGlucose();
    toast.success('Data refreshed');
  };

  if (analyticsError) {
    toast.error(handleApiError(analyticsError));
  }

  const isLoading = analyticsLoading;
  const days = parseInt(period, 10);

  // Aggregate steps by date
  const stepsByDate: Record<string, number> = {};
  stepEntries.forEach((entry: any) => {
    const d = entry.date || entry.createdAt;
    if (!d) return;
    const dateKey = format(new Date(d), 'MMM dd');
    const steps = typeof entry.steps === 'number' ? entry.steps : Number(entry.steps) || 0;
    stepsByDate[dateKey] = (stepsByDate[dateKey] ?? 0) + steps;
  });
  const sortedStepDates = Object.keys(stepsByDate).sort(
    (a, b) => new Date(a).getTime() - new Date(b).getTime()
  );
  const stepsChartData = {
    labels: sortedStepDates,
    datasets: [
      {
        label: 'Steps',
        data: sortedStepDates.map((d) => stepsByDate[d]),
        borderColor: 'rgb(59, 130, 246)',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        fill: true,
        tension: 0.3,
      },
    ],
  };

  // Glucose trend: sort by time
  const sortedGlucose = [...glucoseReadings].sort((a: any, b: any) => {
    const t1 = new Date(a.readingTime || a.createdAt).getTime();
    const t2 = new Date(b.readingTime || b.createdAt).getTime();
    return t1 - t2;
  });
  const glucoseChartData = {
    labels: sortedGlucose.map((r: any, i: number) => `#${i + 1}`),
    datasets: [
      {
        label: 'Glucose (mg/dL)',
        data: sortedGlucose.map((r: any) => (r.value != null ? Number(r.value) : null)),
        borderColor: 'rgb(34, 197, 94)',
        backgroundColor: 'rgba(34, 197, 94, 0.1)',
        fill: true,
        tension: 0.3,
      },
    ],
  };

  const totalSteps =
    Number(analytics.totalSteps ?? analytics.steps?.total ?? 0) ||
    stepEntries.reduce((s: number, e: any) => s + (Number(e.steps) || 0), 0);
  const totalCaloriesBurnt = Number(
    analytics.totalCaloriesBurnt ?? analytics.calories?.burnt ?? 0,
  );
  const avgGlucose =
    analytics.avgGlucose ?? analytics.glucose?.average ?? analytics.averageGlucose ?? null;
  const totalCalories = Number(
    analytics.totalCalories ?? analytics.calories?.consumed ?? 0,
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 pb-20">
      <div className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={onBack}
              className="flex items-center space-x-2 text-gray-700 hover:text-dropsugar-green transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="font-medium">Back</span>
            </button>
            <h1 className="text-xl font-bold text-gray-900">Historical Data</h1>
            <button
              onClick={handleRefresh}
              className="p-2 text-gray-700 hover:text-dropsugar-green hover:bg-dropsugar-green/10 rounded-lg transition-colors"
              title="Refresh"
              aria-label="Refresh"
            >
              <RefreshCw className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6">
        {/* Period selector */}
        <div className="flex flex-wrap gap-2 mb-6">
          {(['7', '30', '90'] as const).map((p) => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              className={`px-4 py-2 rounded-xl font-medium transition-colors ${
                period === p
                  ? 'bg-dropsugar-green text-white shadow-md'
                  : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
              }`}
            >
              {p} Days
            </button>
          ))}
        </div>

        {isLoading ? (
          <LoadingSkeleton />
        ) : (
          <>
            {/* Summary cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 bg-dropsugar-green/10 rounded-lg">
                    <Footprints className="w-5 h-5 text-dropsugar-green" />
                  </div>
                  <span className="text-sm text-gray-600">Total Steps</span>
                </div>
                <p className="text-2xl font-bold text-gray-900">{totalSteps.toLocaleString()}</p>
              </div>
              <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 bg-orange-50 rounded-lg">
                    <Flame className="w-5 h-5 text-orange-600" />
                  </div>
                  <span className="text-sm text-gray-600">Calories Burned</span>
                </div>
                <p className="text-2xl font-bold text-gray-900">
                  {Number(totalCaloriesBurnt).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                </p>
              </div>
              <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 bg-green-50 rounded-lg">
                    <Droplets className="w-5 h-5 text-green-600" />
                  </div>
                  <span className="text-sm text-gray-600">Avg Glucose</span>
                </div>
                <p className="text-2xl font-bold text-gray-900">
                  {avgGlucose != null ? `${Number(avgGlucose).toFixed(0)} mg/dL` : 'N/A'}
                </p>
              </div>
              <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 bg-purple-50 rounded-lg">
                    <UtensilsCrossed className="w-5 h-5 text-purple-600" />
                  </div>
                  <span className="text-sm text-gray-600">Total Calories</span>
                </div>
                <p className="text-2xl font-bold text-gray-900">
                  {Number(totalCalories).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                </p>
              </div>
            </div>

            {/* Steps chart */}
            {sortedStepDates.length > 0 && (
              <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 mb-8">
                <h3 className="text-lg font-bold text-gray-900 mb-4">Steps Trend</h3>
                <div className="h-64">
                  <Bar
                    data={stepsChartData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: { legend: { display: false } },
                      scales: {
                        y: { beginAtZero: true },
                      },
                    }}
                  />
                </div>
              </div>
            )}

            {/* Glucose chart */}
            {sortedGlucose.length > 0 && (
              <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 mb-8">
                <h3 className="text-lg font-bold text-gray-900 mb-4">Glucose Trend</h3>
                <div className="h-64">
                  <Line
                    data={glucoseChartData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: { legend: { display: false } },
                      scales: {
                        y: { beginAtZero: false },
                      },
                    }}
                  />
                </div>
              </div>
            )}

            {/* Detailed metrics */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Calendar className="w-5 h-5 text-dropsugar-green" />
                Detailed Metrics
              </h3>
              <dl className="space-y-3">
                <div className="flex justify-between py-2 border-b border-gray-100">
                  <dt className="text-gray-600">Average steps per day</dt>
                  <dd className="font-semibold text-gray-900">
                    {days > 0 ? Math.round(totalSteps / days).toLocaleString() : 0}
                  </dd>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-100">
                  <dt className="text-gray-600">Average calories per day</dt>
                  <dd className="font-semibold text-gray-900">
                    {days > 0
                      ? (Number(totalCalories) / days).toLocaleString(undefined, {
                          maximumFractionDigits: 0,
                        })
                      : 0}
                  </dd>
                </div>
                {avgGlucose != null && (
                  <div className="flex justify-between py-2">
                    <dt className="text-gray-600">Average glucose</dt>
                    <dd className="font-semibold text-gray-900">
                      {Number(avgGlucose).toFixed(0)} mg/dL
                    </dd>
                  </div>
                )}
              </dl>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
